<template>
  <div>
    <b-modal id="visit-pay-modal"
             :size="justMadePayment ? 'md' : 'xl'"
             v-if="canBeShown"
             :title="modalTitle"
             title-class="compact-modal-title"
             header-class="new-design-modal-header"
             footer-class="new-design-modal-footer"
             body-class="scrollable-modal-body"
             content-class="modal-content-full-height"
             centered
             @show="onShow"
             @shown="attachSummaryScrollObserver"
             @hide="onModalHide"
             @hidden="detachSummaryScrollObserver">
      <template #modal-title>
        <div v-if="paymentFor === 'visit' && visit"
             class="position-sticky d-flex flex-row gap-1 align-items-center">
          <div class="flex-shrink-0">{{ isMobile ? $t('payment_from') : $t('services_payment_from') }}</div>
          <div>{{ formatDate(visit.start) }}</div>
          <div class="text-muted">{{ formatTime(visit.start) }}</div>
          <div class="text-muted small pointer"
               @click="scrollToHistory"
               v-if="payments.length && !justMadePayment">
            {{ $t('payment_history') }}
          </div>
          <div v-if="patient"
               class="nowrap overflow-hidden"
               style="text-overflow: ellipsis;"> - {{ surnameWithInitials(patient) }}</div>
        </div>
        <div v-if="paymentFor === 'material'">
          {{ $t('sale_of_goods') }}
        </div>
      </template>

      <div class="patient-visit-wrap-new">

        <div  v-if="!justMadePayment">

          <div v-if="forVisit">
            <VisitPayAddTooth :readonly="hadPayments"/>
            <PatientVisitServicesSummary :showTitle="false"
                                         view="payment"
                                         class="mt-4"/>
          </div>

          <div v-if="paymentFor === 'material'"
               class="parent-gray-label">

            <div class="row">
              <div class="col-md-6">
                <ServiceMultiSelect :label="$t('seller')"
                                    :dblclick="false"
                                    model="staff"
                                    :multiple="false"
                                    title="full_name"
                                    :dbsearch="true"
                                    v-model="acceptedBy"/>
              </div>
              <div class="col-md-6">
                <ServiceMultiSelect :label="$t('patient')"
                                    :dblclick="false"
                                    model="patient"
                                    :multiple="false"
                                    title="full_name"
                                    @input="$store.commit('setPatient', $event)"
                                    :dbsearch="true"
                                    v-model="patientByingMaterials"/>
              </div>
            </div>
            <SelectedMaterialsTable :materials="materialsForSale"
                                    :editable="!cannotChange"
                                    @itemDeleted="materialsForSale.splice($event, 1)"/>
            <label class="form-label">{{ $t('add_good') }}</label>
            <Autocomplete :placeholder="$t('enter_title_or_barcode')"
                          model="material"
                          class="template-like"
                          :autocompleteAdditionalParams="{ 'available_for_sale': 1 }"
                          v-model="autocompleteMaterial"/>
          </div>

          <div class="row mt-3">

            <div class="col-12">
              <div class="d-flex gap-3 parent-gray-label"
                   v-if="initiated">
                <PatientVisitAddServiceGeneral class="w-100"
                                               v-if="editMode && forVisit"
                                               :showSelectedBlock="true"
                                               :allowDropdownAbove="false"
                                               :showSelectFromPrice="true"/>
              </div>
            </div>

            <div class="col-md-6 col-lg-8 col-12">

              <div class="d-flex gap-3 parent-gray-label"
                   v-if="!insuranceCompany && initiated">
                <PatientVisitAdditionalDiscount :readonly="!editMode"
                                                class="w-100"
                                                :costOfMaterialsForSale="costOfMaterialsForSale"
                                                @discountUpdated="discountUpdated"
                                                :forVisit="forVisit"
                                                :cannotChange="false"/>
              </div>

              <div class="d-flex gap-3 parent-gray-label"
                   v-if="!insuranceCompany && initiated">
                <div v-if="bonusesEnabled && rights.includes('accept_bonus_payments')"
                     class="w-100 form-group">
                  <InputGroup type="number"
                              formLabelClass="gray-label"
                              class="mb-0"
                              v-if="!forVisit || visit.is_paid != '1'"
                              :label="$t('bonuses_payment')"
                              v-model="bonus"/>
                  <div style="opacity: 0.75">
                    <small>
                      {{ $t('available') }}: {{ formatNumber(bonusForBranch) }}
                      <span class="px-1">|</span>
                      {{ $t('can_be_written_off') }}: <span @click="bonus = maxCanBePaidWithBonuses"
                                                            class="text-underlined pointer">{{ formatNumber(maxCanBePaidWithBonuses) }}</span>
                    </small>
                  </div>
                </div>
              </div>

              <InputGroup v-model="comment"
                          formLabelClass="gray-label"
                          v-if="!forVisit || visit.is_paid != '1'"
                          :show-errors="true"
                          validation-id="comment"
                          :label="$t('comment')"/>

            </div>


            <div class="text-right col-md-6 col-lg-4 col-12">
              <VisitPayTotals :bonus="bonus"
                              :paymentFor="paymentFor"
                              :materialsDiscountAbsolute="materialsDiscountAbsolute"
                              :costOfMaterialsForSale="costOfMaterialsForSale"
                              :paidWithBonuses="paidWithBonuses"
                              :leftToPay="leftToPay"/>
            </div>
          </div>

          <hr class="dashed" v-if="!forVisit || visit.is_paid != '1'"/>

          <div class="mb-2">
            <div class="d-inline-flex status-badge status-active gap-1 small"
                 v-if="paymentMethodObjects.length && forVisit">
              <span class="font-weight-bold">{{ $t('left_to_pay2') }}:</span>
              <span>{{ formatCurrency(leftToPayAfterTotalAmount) }}</span>
            </div>
          </div>

          <div class="row align-items-center"
               v-if="!userWantsToPayEverythingWithBonus">
            <div class="col-md parent-gray-label">
              <div v-for="(paymentObject, ind) in paymentMethodObjects"
                   :key="`payment-input-${ind}-${amountKey}`">
                <PaymentInput :visit="visit"
                              :canBeDeleted="paymentMethodObjects.length > 1"
                              v-if="initiated && !justMadePayments.length"
                              ref="paymentInput"
                              :payment-for="paymentFor"
                              :mainPayment="ind === 0"
                              :totalCost="visitCostServicesWithDiscount"
                              :leftToPayAfterTotalAmount="leftToPayAfterTotalAmount"
                              :leftToPay="leftToPay"
                              :excludePayOptions="paymentMethodObjects.map(x => Number(x.pay_method)).filter(x => x !== Number(paymentObject.pay_method))"
                              :canBeProcessedWithoutPayment="canBeProcessedWithoutPayment"
                              @changePPO="(val) => ppo = val"
                              @resetBonus="bonus = 0"
                              @removeOtherPayments="paymentMethodObjects = [paymentMethodObjects[0]]"
                              @paymentMethodDelete="paymentMethodObjects.splice(ind, 1); amountKey++"
                              v-model="paymentMethodObjects[ind]"/>
              </div>

              <a @click="addPayment"
                 v-if="paymentMethodObjects.length < 2 && !hasWithoutPayMethod && allowsAddToInsurance"
                 class="btn btn-themed btn-themed-squared gray">
                <PlusSvg class="svg-black svg-size-sm"/>
              </a>

            </div>

          </div>

          <div class="small text-danger mt-3"
               v-if="needsToSelectPolisBeforeUsingInsurance">
            {{ $t('visit_should_have_polis_set_in_order_to_use_insurance_payment') }}
          </div>

          <div v-if="showAdvanceBalance"
               class="under-form-group mt-3 d-md-block d-none">
            <div>
              <div class="font-weight-bold small pointer collapse-btn"
                   v-spaceduck-collapse="'collapse-deposit-table'">
                {{ $t('deposit') }}: {{ formatCurrency(patient.advance_calculated) }}
                <ArrowDownSvg class="ml-2"/>
              </div>
              <spaceduck-collapse id="collapse-deposit-table">
                <DepositByAccountsTable class="small"
                                        v-if="deposit_accounts.length"
                                        :patient="patient"/>
              </spaceduck-collapse>
            </div>
          </div>

          <VisitPayHistory v-if="forVisit"/>

        </div>

        <JustMadePayment :payment="justMadePayment"
                         :payments="justMadePayments"
                         :ppo="ppo"
                         :amountNumeric="amountNumeric"
                         :leftToPay="leftToPay"
                         @acceptAnotherPayment="acceptAnotherPayment"/>
      </div>
      <template #modal-footer>
        <PaymentPrintInvoiceAkt :forVisit="forVisit"
                                :justMadePayment="justMadePayment"
                                :forcePPO="forcePPO"
                                :ppo="ppo"
                                :ppoAccount="ppoAccount"
                                :sendFiscalToEmail="sendFiscalToEmail"
                                @update:ppo="ppo = $event"
                                @update:ppoAccount="ppoAccount = $event"
                                @update:sendFiscalToEmail="sendFiscalToEmail = $event">
          <button v-if="(versionCount > 0 || servicesChanged) && !justMadePayments.length"
                  :disabled="loading"
                  @click="saveWithoutPay"
                  class="btn-themed btn-themed-squared btn-themed-squared-rounder">
            {{ $t('save') }}
          </button>
          <button :disabled="saveButtonDisabled"
                  v-if="!justMadePayments.length"
                  @click="save"
                  class="btn-themed btn-success btn-themed-squared btn-themed-squared-rounder">
            {{ hasOpenedVisit ? $t('visit_in_progress') : $t('make_payment') }}
          </button>
        </PaymentPrintInvoiceAkt>
      </template>
    </b-modal>
    <KKMCardPayModal :modal-resolve="payModalAction"
                     :status="kkmAcquiringStatus"/>
    <BonusOTPModal @checked="otpModalResolve"
                   :bonus="bonus"/>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeMount, onBeforeUnmount, provide, ref, watch } from "vue"
import { useStore } from "vuex"
import InputGroup from "@/components/form/InputGroup"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import VisitPayAddTooth from "@/components/parts/patients/VisitPayAddTooth"
import {formatNumber, formatCurrency, formatDate, formatTime} from "@/extensions/filters/filters"
import KKMService from "@/services/kkm.service"
import KKMCardPayModal from "@/components/parts/cashdesk/KKMCardPayModal"
import DepositByAccountsTable from "@/components/parts/patients/DepositByAccountsTable.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"
import JustMadePayment from "@/components/parts/patients/payment/JustMadePayment.vue"
import PaymentPrintInvoiceAkt from "@/components/parts/patients/PaymentPrintInvoiceAkt.vue"
import BonusOTPModal from "@/components/parts/patients/BonusOTPModal.vue"
import PaymentInput from "@/components/parts/patients/payment/PaymentInput.vue"
import {numberStringToNumber, roundToTwoDecimals} from "@/services/number.service"
import PlusSvg from '@/assets/svg-vue/header/plus_t.svg'
import ArrowDownSvg from "@/assets/svg-vue/general/arrow_down.svg"
import {LogService} from "@/services/api/log.api.service"
import Autocomplete from "@/components/parts/general/Autocomplete.vue"
import SelectedMaterialsTable from "@/components/parts/warehouse/SelectedMaterialsTable.vue"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect.vue"
import PatientVisitServicesSummary from "@/components/parts/patients/visit/teethTab/PatientVisitServicesSummary.vue"
import PatientVisitAddServiceGeneral from "@/components/parts/patients/visit/teethTab/PatientVisitAddServiceGeneral.vue"
import VisitPayTotals from "@/components/parts/patients/pay/VisitPayTotals.vue"
import PatientVisitAdditionalDiscount
  from "@/components/parts/patients/visit/teethTab/PatientVisitAdditionalDiscount.vue"
import VisitPayHistory from "@/components/parts/patients/pay/VisitPayHistory.vue"
import SpaceduckCollapse from "@/components/parts/general/SpaceduckCollapse.vue"
import {surnameWithInitials} from "@/services/text.service"
import {customConfirm} from "@/services/confirm.service"

const props = defineProps({
  paymentFor: {
    type: String,
    default: 'visit'
  }
})
const emit = defineEmits(['hide'])

const store = useStore()

let i18n = null
let bvModal = null
let noty = null
onBeforeMount(() => {
  const instance = getCurrentInstance()
  i18n = instance?.ctx?.$i18n
  bvModal = instance?.ctx?._bv__modal
  noty = instance?.appContext?.app?.noty
})

const initiated = ref(false)
const versionCount = ref(0)
const paymentMethodObjects = ref([{}])
const bonus = ref(0)
const materialsDiscountAbsolute = ref(0)
const fixedVisitId = ref(null)
const additional_discount = ref(null)
const justMadePayments = ref([])
const rateType = ref('absolute')
const amountKey = ref(1)
const loaderWatcher = ref(false)
const ppo = ref(false)
const ppoAccount = ref(null)
const sendFiscalToEmail = ref(false)
const comment = ref('')
const payModalAction = ref(null)
const kkmAcquiringStatus = ref(null)
const payMethodDetails = ref(null)
const kkmCardPaymentId = ref(null)
const patientByingMaterials = ref(null)
const materialsForSale = ref([])
const autocompleteMaterial = ref(null)
const acceptedBy = ref(null)
const otpModalResolve = ref(() => {})
const editMode = ref(true)
const servicesChanged = ref(false)

// const discountInput = ref(null)
const paymentInput = ref([])

const loading = computed(() => store.state.dom.loading)
const patient = computed(() => store.state.patient.patient)
const clinic = computed(() => store.state.auth.clinic)
const visit = computed(() => store.state.patient.visit)
const rights = computed(() => store.state.auth.rights)
const branch = computed(() => store.state.auth.branch)
const services = computed(() => store.getters.recordTeethServices)
const deposit_accounts = computed(() => store.state.auth.deposit_accounts)
const user = computed(() => store.state.auth.user)
const usesBonuses = computed(() => store.getters.usesBonuses)
const kkmEnabled = computed(() => store.getters.kkmEnabled)
const dynamicConfigValue = computed(() => store.getters.dynamicConfigValue)
const getClinicAdditional = computed(() => store.getters.getClinicAdditional)
const aqsiAccounts = computed(() => store.getters.aqsiAccounts)
const cashdeskSystemSelected = computed(() => store.getters.cashdeskSystemSelected)
const isMobile = computed(() => store.state.dom.isMobile)
const visitCostServicesWithoutDiscount = computed(() => {
  if(!forVisit.value) return costOfMaterialsForSale.value
  return store.getters.visitCostServicesWithoutDiscount
})
const visitCostServicesWithDiscount = computed(() => {
  if(!forVisit.value) return costOfMaterialsForSale.value
  return store.getters.visitCostServicesWithDiscount
})
const costOfMaterialsForSale = computed(() => (materialsForSale.value || []).reduce((acc, curr) => acc + (curr.amount * curr.sale_price), 0))
const teethObjects = computed(() => store.getters.recordTeethObjects)
const allTeethServices = computed(() => (teethObjects.value || []).flatMap(t => copyObject(t.services) || []))

const bonusesEnabled = computed(() => {
  if(!usesBonuses.value) return false
  if(props.paymentFor === 'material') return patientByingMaterials.value?.use_bonuses
  return patient.value?.use_bonuses
})
const modalTitle = computed(() => {
  if(props.paymentFor === 'visit') {
    return visit.value ? (i18n.t('services_payment_from')+' '+visit.value.readableStart) : ''
  }
  if(props.paymentFor === 'material') {
    return i18n.t('sale_of_goods')
  }
  return ''
})
const forVisit = computed(() => props.paymentFor === 'visit')

const canBeShown = computed(() => {
  if(props.paymentFor === 'visit' && visit.value) return true
  if(props.paymentFor === 'material') return true
  return false
})

const canBePaid = computed(() => {
  if(props.paymentFor === 'material') {
    return paymentMethodObjects.value.every(object => payObjectDataValid(object))
        && leftToPay.value === amountNumeric.value && bonusValid.value
  }
  return paymentMethodObjects.value.every(object => payObjectDataValid(object))
      && leftToPay.value >= amountNumeric.value
      && (amountWithBonus.value > 0 || hasWithoutPayMethod.value)
      && bonusValid.value
})

const totalDiscounted = computed(() => {
  return Math.round((visitCostServicesWithDiscount.value - discountAbsoluteSum.value) * 100) / 100
})

const payments = computed(() => visit.value?.payments || [])
const paid = computed(() => {
  if(!forVisit.value) return false
  return !!(!visit.value || (visit.value.price > 0 && (visit.value.debt == 0 || payments.value.length)))
})
const cannotChange = computed(() => paid.value)
const cannotChangeServices = computed(() => !rights.value.includes('add_edit_visit_services'))
const hasServices = computed(() => (visit.value?.actual_services?.length || 0) > 0 || visitCostServicesWithoutDiscount.value > 0)
const insuranceCompany = computed(() => visit.value?.insurance_company)
const leftToPay = computed(() => Math.round((totalDiscounted.value - (visit.value?.paidSum || 0) - Number(bonus.value)) * 100) / 100)
const leftToPayAfterTotalAmount = computed(() => Math.max(0, leftToPay.value - amountNumeric.value))
const discountAbsoluteSum = computed(() => {
  if(forVisit.value) return visit.value?.additional_discount || 0
  return materialsDiscountAbsolute.value
})
const amountNumeric = computed(() => {
  let sum = 0
  paymentMethodObjects.value.forEach(item => {
    sum += numberStringToNumber(item.amount)
  })
  return sum
})
const amountWithBonus = computed(() => amountNumeric.value + Number(bonus.value))
const bonusForBranch = computed(() => {
  if(props.paymentFor === 'material') return Number(patientByingMaterials.value?.total_bonuses || 0)
  return Number(patient.value?.total_bonuses || 0)
})
const coefficientCanBePaidWithBonus = computed(() => (clinic.value?.clinic_additional?.bonus_use || 0) / 100)
const shareCanBePaidWithBonus = computed(() => Math.floor(totalDiscounted.value * coefficientCanBePaidWithBonus.value))
const maxCanBePaidWithBonuses = computed(() => Math.max(0, Math.min(Number(shareCanBePaidWithBonus.value) - Number(visit.value?.bonusesUsedToPay || 0), bonusForBranch.value)))
const bonusValid = computed(() => {
  if(bonus.value === 0) return true
  if(Number(bonus.value) > bonusForBranch.value) return false
  return (Number(bonus.value) + Number(visit.value?.bonusesUsedToPay || 0)) <= shareCanBePaidWithBonus.value
})
const hasOpenedVisit = computed(() => !!visit.value?.opened_visit && !getClinicAdditional.value('accept_payments_for_nonclosed_visit'))
const canBeProcessedWithoutPayment = computed(() => {
  if(userWantsToPayEverythingWithBonus.value) return true
  return hasServices.value && (totalDiscounted.value - (visit.value?.paidSum || 0)) === 0
})
const hasWithoutPayMethod = computed(() => paymentMethodObjects.value.some(obj => Number(obj.pay_method) === 7))
const canPayEverythingWithBonus = computed(() => coefficientCanBePaidWithBonus.value >= 1)
const userWantsToPayEverythingWithBonus = computed(() => {
  if(!forVisit.value) return false
  return canPayEverythingWithBonus.value && leftToPay.value <= bonus.value
})
const allowsAddToInsurance = computed(() => !hasInsurancePayMethod.value || getClinicAdditional.value('payments_for_insurance_visits'))
const hasInsurancePayMethod = computed(() => paymentMethodObjects.value.some(obj => Number(obj.pay_method) === 4))
const aqsiSendServices = computed(() => dynamicConfigValue.value('aqsi_send_services') || false)
const kkmSendServices = computed(() => dynamicConfigValue.value('kkm_send_services', branch.value) || false)
const fiscalIsAQSI = computed(() => cashdeskSystemSelected.value === 'aqsi')
const hadPayments = computed(() => payments.value.length > 0)
const paidWithBonuses = computed(() => payments.value.reduce((a, b) => a + (Number(b.forma) === 6 ? Number(b.paid) : 0), 0))
const hadAqsiPayments = computed(() => {
  const payments = visit.value?.payments || []
  return (payments.filter(payment => payment.fiscal_vendor === 'aqsi' && payment.local_uuid)).length > 0
})
const hadKkmPayments = computed(() => {
  const payments = visit.value?.payments || []
  return (payments.filter(payment => payment.fiscal_vendor === 'kkm')).length > 0
})
const forcePPO = computed(() => {
  if(!hadPayments.value) return null // if no payments were made, we do not force ppo
  if(fiscalIsAQSI.value && aqsiSendServices.value) {
    return hadAqsiPayments.value // means had payments and is sending services, so should be fiscal if were fiscal or not if previous payments were not fiscal
  }
  if(kkmEnabled.value && kkmSendServices.value) {
    return hadKkmPayments.value // means had payments and is sending services, so should be fiscal if were fiscal or not if previous payments were not fiscal
  }
  return null // if it's not aqsi or kkm, or they do not send services in cheque, we do not force ppo
})
const needsToSelectPolisBeforeUsingInsurance = computed(() => paymentMethodObjects.value.some(obj => obj.needsToSelectPolisBeforeUsingInsurance))
const justMadePayment = computed(() => justMadePayments.value.length ? justMadePayments.value[justMadePayments.value.length - 1] : null)
const saveButtonDisabled = computed(() => {
  if(props.paymentFor === 'material') {
    return loading.value || !patient.value || !canBePaid.value
  }
  return loading.value
      || !canBePaid.value
      || hasOpenedVisit.value
      || justMadePayments.value.length
      || visit.value?.is_paid == '1'
      || needsToSelectPolisBeforeUsingInsurance.value
})
const showAdvanceBalance = computed(() => {
  if(!patient.value || insuranceCompany.value) return false
  if(props.paymentFor === 'material') return true
  return visit.value && visit.value.is_paid != '1'
})

provide('cannotChange', cannotChange)
provide('cannotChangeServices', cannotChangeServices)
provide('versionCount', versionCount)
provide('editMode', editMode)
provide('paymentMethodObjects', paymentMethodObjects)

const saveWithoutPay = async () => {
  const res = await PatientService.payForVisitNew(
      patient.value.id,
      fixedVisitId.value ? fixedVisitId.value : visit.value.id,
      {
        onlySave: true,
        changedServices: true,
        record: visit.value,
      })
  noty.info(i18n.t('success_message'))
  store.commit('incrementDataTableCounter')
  versionCount.value = 0
  servicesChanged.value = false
  visit.value.actual_services = copyObject(res.data.actual_services)
}
const save = async () => {
  try {
    loaderWatcher.value = true
    if(!(await checkForBonusOTP())) return
    const madePayments = []
    for(const [index, paymentMethodObject] of paymentMethodObjects.value.entries()) {
      const isLast = index === paymentMethodObjects.value.length - 1
      const amount = numberStringToNumber(paymentMethodObject.amount)
      const amountOfAllPayments = paymentMethodObjects.value.reduce((a, o) => a + numberStringToNumber(o.amount), 0)
      if(amount === 0 && paymentMethodObject.pay_method === 4) continue
      if(ppo.value && kkmEnabled.value) {
        // console.log(Number(paymentMethodObject.pay_method))
        // console.log((dynamicConfigValue.value('kkm_acquiring_methods', branch.value) || []))
        payMethodDetails.value = (await EloquentService.show('pay_method', paymentMethodObject.pay_method))?.data?.custom_data || null
        if((dynamicConfigValue.value('kkm_acquiring_methods', branch.value) || []).includes(Number(paymentMethodObject.pay_method))) {
          // if(ppo.value) {
          kkmAcquiringStatus.value = null
          bvModal.show('kkm-card-pay-modal')
          const r = await KKMService.payByPaymentCard(amount, payMethodDetails.value?.kkm_acquiring || 0, payMethodDetails.value?.kkm_inn || "")
          LogService.sendLog({
            type: 'kkm',
            log: r,
            additionalData: {
              action: 'kkmAcquiring',
              clinicId: clinic.value?.id,
              patientId: patient.value?.id,
              recordId: fixedVisitId.value || visit.value?.id,
              amount: amount
            }
          }).then().catch()
          if(r.error) {
            noty.error(r.error)
            kkmAcquiringStatus.value = 2
          } else {
            kkmAcquiringStatus.value = r.data.Status
            kkmCardPaymentId.value = r.data.UniversalID
          }
          bvModal.hide('kkm-card-pay-modal')
          const paidByCard = kkmAcquiringStatus.value === 0
          if(!paidByCard) return
        }
      }
      const res = await PatientService.payForVisitNew(
          patient.value.id,
          fixedVisitId.value ? fixedVisitId.value : (visit.value?.id || null),
          {
            record: visit.value,
            additional_discount: discountAbsoluteSum.value,
            additional_discount_percent: visit.value?.additional_discount_percent || 0,
            amount,
            amountOfAllPayments,
            bonus: isLast ? Number(bonus.value) : 0,
            pay_method: paymentMethodObject.pay_method,
            deposit_account_id: paymentMethodObject.deposit_account_id,
            family_account: paymentMethodObject.parentAccount || null,
            family_account_deposit_id: paymentMethodObject.parentAccountDepositAccountId || null,
            family_account_branch_id: paymentMethodObject.parentAccountObject?.branchId || null,
            onlySave: false,
            ppo: isLast ? ppo.value : false,
            batchPayments: [
              ...madePayments.map(p => ({ amount: p.paid, payMethodId: p.forma, id: p.id })),
              { amount, payMethodId: paymentMethodObject.pay_method }
            ],
            changedServices: index === 0 ? versionCount.value > 0 : false,
            closeVisit: index === 0 ? versionCount.value > 0 : false,
            comment: comment.value,
            insurance_company_id: paymentMethodObject.insurance_company_id,
            ppoAccount: ppoAccount.value,
            acceptedBy: acceptedBy.value?.id,
            materialsForSale: materialsForSale.value,
            paymentFor: props.paymentFor,
            kkmCardPaymentId: kkmCardPaymentId.value
          })
      if(res?.data?.id) {
        madePayments.push(res.data)
      }
    }
    justMadePayments.value = copyObject(madePayments)
    noty.info(i18n.t('success_message'))
    kkmCardPaymentId.value = null
    if(justMadePayments.value.length > 0) {
      if(ppo.value && kkmEnabled.value) {
        await kkmPrintCheque()
      }
    } else {
      bvModal.hide('visit-pay-modal')
    }
    store.commit('incrementDataTableCounter')
  } catch (e) {
    console.log(e)
  } finally {
    loaderWatcher.value = false
    await nextTick()
    store.commit('setLoading', false)
  }
}
const acceptAnotherPayment = async () => {
  initiated.value = false
  await nextTick()
  await store.dispatch('updatePatientDataForPayment', patient.value)
  onHide(false)
  await onShow()
  if(forcePPO.value) ppo.value = true
}

const checkForBonusOTP = async () => {
  if(bonus.value <= 0) return true
  if(!getClinicAdditional.value('confirm_bonuses_through_bot')) return true
  bvModal.show('bonus-otp-modal')
  return new Promise((resolve) => {
    otpModalResolve.value = resolve
  })
}

const onShow = async () => {
  if(forVisit.value) {
    if(!visit.value || !patient.value) return
    fixedVisitId.value = visit.value.id
    rateType.value = 'absolute'
    await store.dispatch('getClientVisit', {
      id: patient.value.id,
      visit_id: visit.value.id
    })
    if(visit.value.additional_discount_percent) {
      rateType.value = 'percent'
      additional_discount.value = visit.value.additional_discount_percent
    } else {
      rateType.value = 'absolute'
      additional_discount.value = visit.value.additional_discount
    }
    await nextTick()
  }
  await store.dispatch('getDepositAccounts')
  acceptedBy.value = copyObject(user.value)
  initiated.value = true
}

let skipUnsavedChangesCheck = false

const onModalHide = async (bvModalEvent) => {
  if(skipUnsavedChangesCheck || justMadePayments.value.length) {
    onHide()
    return
  }
  if(bvModalEvent?.trigger && (versionCount.value > 0 || servicesChanged.value)) {
    bvModalEvent.preventDefault()
    const pressedSave = await customConfirm({
      title: i18n.t('there_are_unsaved_changes'),
      confirmTitle: i18n.t('save'),
      cancelTitle: i18n.t('do_not_save'),
      cancelBtnClass: 'btn-outline-danger'
    })
    if(pressedSave === null) return
    if(pressedSave) {
      await saveWithoutPay()
    }
    skipUnsavedChangesCheck = true
    bvModal.hide('visit-pay-modal')
    skipUnsavedChangesCheck = false
    return
  }
  onHide()
}

const onHide = (full = true) => {
  if(full) {
    store.commit('setVisit', null)
    store.commit('nullifyTreatmentCard')
    initiated.value = false
  }
  ppo.value = false
  paymentMethodObjects.value = [{}]
  bonus.value = 0
  versionCount.value = 0
  servicesChanged.value = false
  additional_discount.value = null
  patientByingMaterials.value = null
  materialsForSale.value = []
  justMadePayments.value = []
  comment.value = ''
  store.commit('cleanValidationErrors')
  emit('hide')
}

const payObjectDataValid = (payObject) => {
  const payMethod = Number(payObject.pay_method)
  const payAmount = numberStringToNumber(payObject.amount)
  if(canBeProcessedWithoutPayment.value && payMethod === 7) return true
  return payMethod >= 0
      && (payAmount > 0 || payMethod === 4)
      && (payMethod !== 100 || payObject.parentAccount)
      && !(payMethod === 5 && payAmount > patient.value.advance_calculated)
      && !(payMethod === 6 && payAmount > bonusForBranch.value)
}

const kkmPrintCheque = async () => {
  const chequeData = {
    totalPrice: Number(justMadePayment.value.price || justMadePayment.value.paid), // or condition is for payment for materials
    totalPaid: Number(justMadePayment.value.paidSum || justMadePayment.value.paid), // we take last payment, so this sum is good if taken from last payment
    payId: justMadePayment.value.id,
    amount: justMadePayments.value.reduce((sum, item) => sum + Number(item.paid), 0),
    paymentsDetailed: justMadePayments.value.map(({ paid, pay_method }) => ({
      amount: paid,
      payMethodId: Number(pay_method?.id || 100)
    })),
    payMethod: justMadePayment.value.forma,
    services: copyObject(services.value),
    numDevice: payMethodDetails.value?.kkm_fiscal || 0,
    email: sendFiscalToEmail.value ? patient.value.email : null,
    bonus: Number(bonus.value),
    paymentFor: props.paymentFor,
    materialsForSale: materialsForSale.value
  }
  const res = await KKMService.printCheque(chequeData)
  LogService.sendLog({
    type: 'kkm',
    log: res,
    additionalData: {
      action: 'printCheque',
      // chequeData,
      clinicId: clinic.value?.id,
      recordId: fixedVisitId.value || visit.value?.id,
      paymentId: justMadePayment.value.id
    }
  }).then().catch()

  if(res?.error) {
    noty.error(i18n.t('error_printing_cheque') + ': ' + res.error)
  }
  if(res) {
    for(const jmPayment of justMadePayments.value) {
      await PatientService.setUUIDForPayment(jmPayment.id, {
        uuid: res.data?.CheckNumber || null,
        allData: res.data
      })
    }
  }
}

const addPayment = () => {
  const obj = {}
  if(paymentMethodObjects.value.length === 1) {
    obj.amount = roundToTwoDecimals(Math.max(0, leftToPay.value - amountNumeric.value))
  }
  paymentMethodObjects.value.push(obj)
}

const discountUpdated = (params) => {
  if(!forVisit.value) {
    materialsDiscountAbsolute.value = params?.absolute || 0
  }
}

const scrollToHistory = () => {
  const element = document.getElementById("visit-payments-history")
  if(element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" })
  }
}

// keeps the "add service" dropdown visually pinned when the services summary above it grows/shrinks
let summaryResizeObserver = null
let summaryLastHeight = null

const detachSummaryScrollObserver = () => {
  if(summaryResizeObserver) {
    summaryResizeObserver.disconnect()
    summaryResizeObserver = null
  }
  summaryLastHeight = null
}

const attachSummaryScrollObserver = () => {
  detachSummaryScrollObserver()
  const summaryEl = document.getElementById('patient-visit-services-summary')
  const scrollEl = summaryEl?.closest('.scrollable-modal-body')
  if(!summaryEl || !scrollEl) return
  summaryLastHeight = summaryEl.offsetHeight
  summaryResizeObserver = new ResizeObserver(() => {
    const newHeight = summaryEl.offsetHeight
    const delta = newHeight - summaryLastHeight
    if(delta !== 0) {
      scrollEl.scrollTop += delta
      summaryLastHeight = newHeight
    }
  })
  summaryResizeObserver.observe(summaryEl)
}

watch(justMadePayment, () => nextTick(() => attachSummaryScrollObserver()))

onBeforeUnmount(() => {
  detachSummaryScrollObserver()
})

watch(loading, (val) => {
  if(loaderWatcher.value && val === false) {
    nextTick(() => store.commit('setLoading', true))
  }
})

watch(rateType, (val, oldval) => {
  if(oldval && initiated.value) {
    let rate = additional_discount.value
    let newRate = null
    let cost = visitCostServicesWithDiscount.value
    if(val === 'percent') {
      newRate = cost > 0 ? ((rate / cost) * 100) : 0
    }
    if(val === 'absolute') {
      newRate = cost > 0 ? (rate * cost / 100) : 0
    }
    additional_discount.value = newRate
  }
})

watch(ppo, (val) => {
  if(val && aqsiAccounts.value.length > 1) {
    ppoAccount.value = aqsiAccounts.value[0].key
  } else {
    ppoAccount.value = null
  }
  if(forcePPO.value !== null && val !== forcePPO.value) {
    ppo.value = forcePPO.value
  }
})

watch(forcePPO, (val) => {
  if(val === true) {
    ppo.value = true
  }
  if(val === false) {
    ppo.value = false
  }
}, { immediate: true })

watch(userWantsToPayEverythingWithBonus, (val) => {
  if(val) {
    paymentMethodObjects.value[0].pay_method = 7
    paymentMethodObjects.value[0].amount = 0
    paymentMethodObjects.value = [paymentMethodObjects.value[0]]
  } else {
    paymentMethodObjects.value.forEach(paymentMethod => {
      if(Number(paymentMethod.pay_method) === 7) {
        paymentMethod.pay_method = 1
        paymentMethod.amount = 0
      }
    })
  }
})

watch(autocompleteMaterial, (val) => {
  if(val) {
    const ind = materialsForSale.value.findIndex(x => x.id === val.id)
    if(ind === -1) {
      val.amount = 1
      materialsForSale.value.push(val)
    } else {
      materialsForSale.value[ind].amount++
    }
    autocompleteMaterial.value = null
  }
})

watch(patientByingMaterials, (val) => {
  if(val) {
    EloquentService.show('patient', val.id).then(res => {
      store.commit('setPatient', res.data)
    }).catch(err => {
      console.log(err)
    })
  }
})

watch(() => JSON.stringify([
  allTeethServices.value,
  (teethObjects.value || []).map(t => [t.materials || [], t.packages || []])
]), (newVal, oldVal) => {
  if (!initiated.value) return
  if (newVal !== oldVal) {
    servicesChanged.value = true
  }
})
</script>

<style scoped lang="scss">
.btn-themed.show {
  opacity: 1;
}
</style>
